import { env } from '@/config/env';

/** GIPHY GIF search (Tenor shut down June 30). The key is supplied via
 *  VITE_GIPHY_KEY (with a working default); the chosen GIF's URL is stored
 *  directly in `community_messages.media` (external URL, no upload needed). */

export interface Gif {
  id: string;
  /** Full-size GIF URL — what we store/send. */
  url: string;
  /** Small preview URL for the picker grid. */
  preview: string;
  description: string;
}

export const gifConfigured = (): boolean => Boolean(env.giphyKey);

interface GiphyImage {
  url?: string;
}
interface GiphyResult {
  id: string;
  title?: string;
  images?: {
    original?: GiphyImage;
    downsized?: GiphyImage;
    fixed_width?: GiphyImage;
    fixed_width_small?: GiphyImage;
    fixed_height_small?: GiphyImage;
  };
}

async function request(path: string, params: Record<string, string>): Promise<Gif[]> {
  if (!env.giphyKey) return [];
  const qs = new URLSearchParams({
    api_key: env.giphyKey,
    limit: '24',
    rating: 'pg-13',
    ...params,
  });
  const res = await fetch(`https://api.giphy.com/v1/gifs/${path}?${qs.toString()}`);
  if (!res.ok) throw new Error(`GIPHY request failed (${res.status})`);
  const json = (await res.json()) as { data?: GiphyResult[] };
  return (json.data ?? [])
    .map((r) => ({
      id: r.id,
      url: r.images?.original?.url ?? r.images?.downsized?.url ?? r.images?.fixed_width?.url ?? '',
      preview:
        r.images?.fixed_width_small?.url ??
        r.images?.fixed_height_small?.url ??
        r.images?.fixed_width?.url ??
        r.images?.original?.url ??
        '',
      description: r.title || 'GIF',
    }))
    .filter((g) => g.url && g.preview);
}

export function searchGifs(query: string): Promise<Gif[]> {
  return query.trim() ? request('search', { q: query.trim() }) : request('trending', {});
}
